import { FunctionComponent, RefObject, useEffect, useRef, useState } from 'react';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState } from 'store';
import { createEmptySmsMessage, SmsMessage } from 'model/TwoWaySms';
import {
    clearTwoWaySmsMessagesThunk,
    fetchTwoWaySmsCountByCustomerThunk,
    fetchTwoWaySmsCountThunk,
    fetchTwoWaySmsMessagesThunk,
    saveMessageThunk,
    searchTwoWaySmsThunk
} from 'actions/twoWaySms/twoWaySmsActions';
import useNonInitialEffect from '@versiondos/hooks';
import { TwoWaySmsStatus } from 'reducers/twowaysms/TwoWaySmsState';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useApiClientWrapper } from 'hooks';
import { clearRetrievedClientThunk, getClientProfileOverviewThunk, getClientThunk } from '@spike/clients-action';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { Client as ClientModel } from '@spike/client-model';
import Client from 'pages/Client';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import AES from 'crypto-js/aes';
import { OverFullWindow } from 'components/UI';
import Pet from '@spike/pet-model';

export interface ChatProps {
    clientSelected: number | undefined;
    marketplaceId: number;
    className?: string;
    onChange?: (value: string) => void;
    setClientSelected: (clientSelected: number | undefined) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%'
        },
        chatDisplayContainerNoHeader: {
            width: '100%',
            flexGrow: 1
        },
        chatDisplayContainer: {
            width: '100%',
            maxWidth: '100%',
            flexGrow: 1
        },
        chatInputContainer: {
            width: '100%',
            zIndex: 99,
            borderTop: '#D4D4D4 1px solid'
        },
        clientOverWindow: {
            zIndex: 99,
            [theme.breakpoints.down('sm')]: {
                left: 0
            },
            [theme.breakpoints.only('md')]: {
                left: '65px'
            },
            [theme.breakpoints.only('lg')]: {
                left: '62px'
            },
            [theme.breakpoints.between(1440, 1920)]: {
                left: '80px'
            },
            [theme.breakpoints.only('xl')]: {
                left: '80px'
            }
        }
    })
);
const initMessages = 30;

const secret = `${process.env.REACT_APP_GLOBAL_SECRET}`;

const Chat: FunctionComponent<ChatProps> = props => {
    const apiClientWrapper = useApiClientWrapper();
    const classes = useStyles();
    const dispatch = useDispatch();

    const clientData = useSelector<RootState, ClientModel>(state => state.clients.client!);

    const twoWayStatus = useSelector<RootState, TwoWaySmsStatus>(state => state.twoWaySms.status);
    const messages = useSelector<RootState, Array<SmsMessage>>(state => state.twoWaySms.conversation ?? []);
    const timeZone = useSelector<RootState, string>(state => state.login.auth.timeZone!);

    const [messageText, setMessageText] = useState('');
    const [showMessages, setShowMessages] = useState(initMessages);
    const [messageToSent, setMessageToSent] = useState<SmsMessage | undefined>(undefined);

    const [loading, setLoading] = useState(false);

    const chatScrollRef = useRef<OverlayScrollbarsComponent>(null);

    const [showNewBooking, setShowNewBooking] = useState<boolean>(false);
    const [showNewClient, setShowNewClient] = useState<boolean>(false);
    const [petAdded, setPetAdded] = useState(false);
    const [clientAdded, setClientAdded] = useState(false);
    const [petID, setPetID] = useState<number | undefined>(undefined);
    const [customerID, setCustomerID] = useState<number | undefined>(undefined);

    useEffect(() => {
        setMessagesAsRead(messages);
        dispatch(clearTwoWaySmsMessagesThunk());
        if (clientData && clientData.id) {
            dispatch(fetchTwoWaySmsMessagesThunk(clientData.id, props.marketplaceId));
        }
        return () => {
            dispatch(clearTwoWaySmsMessagesThunk());
        };
    }, []);

    useEffect(() => {
        if (clientData && clientData.id) {
            setMessageToSent(createEmptySmsMessage(clientData.id));
        }
    }, [clientData]);

    useEffect(() => {}, [showNewBooking]);

    useNonInitialEffect(() => {
        if (twoWayStatus === TwoWaySmsStatus.SaveSuccess) {
            setMessageText('');
            if (clientData && clientData.id) {
                dispatch(fetchTwoWaySmsMessagesThunk(clientData.id, props.marketplaceId));
                dispatch(fetchTwoWaySmsCountThunk(props.marketplaceId));
                dispatch(searchTwoWaySmsThunk('', 1, 30, 'all', 'newest', props.marketplaceId));
            }
        }

        if (twoWayStatus === TwoWaySmsStatus.FetchSuccess) {
            scrollToBottomHandler(chatScrollRef);
            setLoading(false);
        }
    }, [twoWayStatus]);

    useNonInitialEffect(() => {
        scrollToBottomHandler(chatScrollRef);
    }, [loading]);

    useNonInitialEffect(() => {
        setMessageText('');
        setShowMessages(initMessages);
        if (clientData && clientData.id) {
            dispatch(fetchTwoWaySmsMessagesThunk(clientData.id, props.marketplaceId));
        }
    }, [clientData]);

    useNonInitialEffect(() => {
        dispatch(clearTwoWaySmsMessagesThunk());
        setMessageText('');
        setShowMessages(initMessages);
        setLoading(true);

        if (props.clientSelected && clientData?.id) {
            dispatch(fetchTwoWaySmsMessagesThunk(clientData.id, props.marketplaceId));
            dispatch(fetchTwoWaySmsCountByCustomerThunk(clientData.id, props.marketplaceId));
        }
    }, [props.marketplaceId]);

    useEffect(() => {
        dispatch(clearTwoWaySmsMessagesThunk());
        setLoading(true);
        if (props.clientSelected) {
            dispatch(getClientThunk(apiClientWrapper, props.clientSelected));
            dispatch(getClientProfileOverviewThunk(apiClientWrapper, props.clientSelected));
            setCustomerID(props.clientSelected);
        } else {
            setCustomerID(undefined);
            dispatch(clearRetrievedClientThunk());
        }
    }, [props.clientSelected]);

    useNonInitialEffect(() => {
        if (clientData && clientData.id) {
            setMessagesAsRead(messages);
        }
    }, [messages]);

    const setMessagesAsRead = (messages: Array<SmsMessage>) => {
        const unReadMessages = messages.filter(message => !message.readAt && message.senderType === 'Customer');
        if (unReadMessages && unReadMessages.length > 0) {
            for (const message of unReadMessages) {
                message.readAt = new Date();
                dispatch(saveMessageThunk(message, props.marketplaceId));
            }
            dispatch(fetchTwoWaySmsCountThunk(props.marketplaceId));
            if (clientData && clientData.id) {
                dispatch(fetchTwoWaySmsMessagesThunk(clientData.id, props.marketplaceId));
                dispatch(fetchTwoWaySmsCountByCustomerThunk(clientData.id, props.marketplaceId));
            }
        }
    };

    const sendMessageHandler = () => {
        if (!messageToSent) return;

        const staffId = store.getState().login.auth.user?.staffId;
        const messageToSentOther = { ...messageToSent };

        if (props.marketplaceId && props.marketplaceId !== 0) {
            messageToSentOther.marketplaceId = props.marketplaceId;
        }
        if (staffId) {
            messageToSentOther.senderId = staffId;
        }
        if (clientData.id) {
            messageToSentOther.clientId = clientData.id;
            messageToSentOther.receiverId = clientData.id;
        }

        messageToSentOther.sentAt = new Date();
        setMessageToSent(messageToSentOther);

        return dispatch(saveMessageThunk(messageToSentOther, props.marketplaceId)) as unknown as Promise<any>;
    };

    const changeMessageHandler = (text: string) => {
        if (messageToSent) {
            const messageToSentOther = { ...messageToSent };
            messageToSentOther.message = text;
            setMessageToSent(messageToSentOther);
            setMessageText(text);
        }
    };

    const scrollToBottomHandler = (ref: RefObject<OverlayScrollbarsComponent>) => {
        if (ref.current) {
            const vp = ref.current.osInstance()?.getElements().viewport;
            if (vp) {
                const scrollHeight = vp.scrollHeight;
                const height = vp.clientHeight;
                const maxScrollTop = scrollHeight - height;
                vp.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
            }
        }
    };

    const scrollHandler = () => {
        if (chatScrollRef.current) {
            const vp = chatScrollRef.current.osInstance()?.getElements().viewport;
            if (vp) {
                if (vp.scrollTop === 0) {
                    const newN = showMessages + 1;
                    setShowMessages(newN);
                    vp.scrollTop = vp.scrollTop + 20;
                }
            }
        }
        return null;
    };

    const clientCardClickHandler = (clientId: number) => {
        if (clientId) {
            const hash = encodeURIComponent(
                AES.encrypt(JSON.stringify({ clientId, petId: undefined }), secret).toString()
            );
            window.open(`/clients/${hash}`, '_blank');
        }
    };

    const bookNowClickHandler = () => {
        if (clientData) {
            setShowNewBooking(true);
        }
    };

    const handleBackNewBooking = () => {
        setShowNewBooking(false);
    };

    const addNewPet = (clientID: number) => {
        setCustomerID(clientID);
        setShowNewBooking(false);
        setShowNewClient(true);
    };

    const backClientHandler = () => {
        setShowNewClient(false);
        setShowNewBooking(true);
    };

    const addNewClient = () => {
        setShowNewBooking(false);
        setShowNewClient(true);
    };

    const handleRemoveClient = () => {
        setClientAdded(false);
        setPetAdded(false);
    };

    const onPetSaveHandler = (pet: Pet) => {
        setCustomerID(pet.clientId);
        setPetID(pet.id);
        setShowNewClient(false);
        setShowNewBooking(true);
    };

    const newBookingView = (
        <CreateBookingDrawer
            parentID={customerID}
            petID={petID}
            onAddClient={addNewClient}
            onAddPet={addNewPet}
            onClose={handleBackNewBooking}
            onBooked={handleBackNewBooking}
            clientAdded={clientAdded}
            petAdded={petAdded}
            onRemoveClient={handleRemoveClient}
            showTabs={false}
        />
    );

    const newClient = (
        <OverFullWindow
            className={classes.clientOverWindow}
            onClose={backClientHandler}
        >
            <Client
                hideHeader={true}
                clientId={customerID}
                isNewBooking={true}
                onClose={backClientHandler}
                onClientSaved={backClientHandler}
                onPetSaved={onPetSaveHandler}
                onDelete={backClientHandler}
            />
        </OverFullWindow>
    );

    return (
        <Box className={classes.root}>
            {props.clientSelected && (
                <ChatHeader
                    clientData={clientData}
                    onClientCardClick={clientCardClickHandler}
                    onBookNowClick={bookNowClickHandler}
                    onBackButton={() => props.setClientSelected(undefined)}
                />
            )}
            <Box className={props.clientSelected ? classes.chatDisplayContainer : classes.chatDisplayContainerNoHeader}>
                {props.clientSelected && (
                    <ChatMessages
                        clientData={clientData}
                        loading={loading}
                        messages={messages.slice(-showMessages)}
                        timeZone={timeZone}
                        chatScrollRef={chatScrollRef}
                        onScroll={scrollHandler}
                    />
                )}
            </Box>
            <Box className={classes.chatInputContainer}>
                <ChatInput
                    isActive={!!props.clientSelected}
                    messageText={messageText}
                    onMessageChange={changeMessageHandler}
                    onSendMessage={sendMessageHandler}
                />
            </Box>
            {showNewBooking && newBookingView}
            {showNewClient && newClient}
        </Box>
    );
};

export default Chat;
